<template>
  <div>
    <add-new-school :is-add-new-school-sidebar-active.sync="isAddNewSchoolSidebarActive" :prefill="prefill"
      @sendschooldata="createSchoolFromRequest" />

    <!-- Status filters -->
    <b-row class="match-height">
      <b-col v-for="tab in statusTabs" :key="tab.value" cols="6" lg="3">
        <b-card no-body class="status-filter-card cursor-pointer" :class="{ 'status-filter-card--active': status === tab.value }"
          :style="status === tab.value ? { borderColor: tab.color } : {}" @click="changeStatus(tab.value)">
          <b-card-body class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0">{{ counts[tab.countKey] || 0 }}</h3>
              <span class="text-muted">{{ tab.label }}</span>
            </div>
            <b-avatar :variant="'light-' + tab.variant" size="42">
              <feather-icon :icon="tab.icon" size="20" />
            </b-avatar>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-card no-body>
      <div class="m-2">
        <b-row class="align-items-center">
          <b-col cols="12" md="6" class="mb-1 mb-md-0">
            <h4 class="mb-25">{{ currentTab.label }} Requests</h4>
            <small class="text-muted">Schools requested from the app by users who could not find their school.</small>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-input-group class="input-group-merge mr-1">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input v-model="searchQuery" placeholder="Search school, contact or city..." />
              </b-input-group>
              <b-button variant="outline-primary" v-b-tooltip.hover title="Refresh" @click="getRequests">
                <feather-icon icon="RefreshCwIcon" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-overlay :show="loading" rounded="sm">
        <b-table-simple hover responsive class="mb-0 school-requests-table">
          <b-thead head-variant="light">
            <b-tr>
              <b-th>School</b-th>
              <b-th class="text-center">Students</b-th>
              <b-th class="text-center">Premium Seats</b-th>
              <b-th class="text-center">Staff / Users</b-th>
              <b-th>Package</b-th>
              <b-th>Contact</b-th>
              <b-th>Requested</b-th>
              <b-th>Status</b-th>
              <b-th class="text-right">Actions</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="request in filteredRequests" :key="request._id">
              <b-td>
                <div class="d-flex align-items-center">
                  <b-avatar variant="light-primary" size="36" class="mr-1" :text="initials(request.schoolName)" />
                  <div>
                    <div class="font-weight-bold text-body">{{ request.schoolName || "-" }}</div>
                    <small class="text-muted d-flex align-items-center">
                      <template v-if="request.city">
                        <feather-icon icon="MapPinIcon" size="12" class="mr-25" />{{ request.city }}
                      </template>
                      <template v-else>No city</template>
                    </small>
                    <small v-if="request.note" class="d-block text-muted request-note" v-b-tooltip.hover
                      :title="request.note">
                      <feather-icon icon="MessageSquareIcon" size="12" class="mr-25" />{{ request.note }}
                    </small>
                  </div>
                </div>
              </b-td>
              <b-td class="text-center">
                <span class="font-weight-bold">{{ formatNumber(request.totalStudents) }}</span>
              </b-td>
              <b-td class="text-center">
                <b-badge v-if="request.premiumSeats > 0" variant="light-warning" pill>
                  <feather-icon icon="StarIcon" size="12" class="mr-25" />{{ request.premiumSeats }}
                </b-badge>
                <span v-else class="text-muted">0</span>
              </b-td>
              <b-td class="text-center">
                <span class="font-weight-bold">{{ formatNumber(request.staffCount) }}</span>
              </b-td>
              <b-td>
                <span v-if="request.packageName">{{ request.packageName }}</span>
                <span v-else class="text-muted">-</span>
              </b-td>
              <b-td>
                <div class="font-weight-bold text-nowrap">{{ request.contactName || "-" }}</div>
                <small v-if="request.contactPhone">
                  <b-link :href="'tel:' + request.contactPhone" class="text-nowrap">
                    <feather-icon icon="PhoneIcon" size="12" class="mr-25" />{{ request.contactPhone }}
                  </b-link>
                </small>
              </b-td>
              <b-td>
                <div class="text-nowrap">{{ formatDate(request.createdAt) }}</div>
                <small v-if="request.requestedByProfileName" class="text-muted text-nowrap">
                  by {{ request.requestedByProfileName }}
                  <template v-if="request.requestedByProfileSerial">({{ request.requestedByProfileSerial }})</template>
                </small>
              </b-td>
              <b-td>
                <b-badge pill :variant="'light-' + statusVariant(request.status)" class="text-capitalize">
                  {{ request.status || "pending" }}
                </b-badge>
                <small v-if="request.reviewedAt" class="d-block text-muted text-nowrap mt-25">
                  {{ formatDate(request.reviewedAt) }}
                </small>
                <small v-if="request.status === 'rejected' && request.rejectReason"
                  class="d-block text-danger request-note" v-b-tooltip.hover :title="request.rejectReason">
                  {{ request.rejectReason }}
                </small>
              </b-td>
              <b-td class="text-right">
                <div v-if="isPending(request)" class="d-flex justify-content-end">
                  <b-button size="sm" variant="primary" class="mr-50 text-nowrap" @click="openCreateSchool(request)">
                    <feather-icon icon="PlusIcon" size="14" class="mr-25" />Create School
                  </b-button>
                  <b-button size="sm" variant="outline-danger" class="text-nowrap" @click="rejectRequest(request)">
                    <feather-icon icon="XIcon" size="14" class="mr-25" />Reject
                  </b-button>
                </div>
                <b-link v-else-if="request.status === 'approved' && request.schoolId"
                  :to="{ path: '/school-management/' + request.schoolId }" class="text-nowrap"
                  @click="sendPropsToSchoolGrade(request.schoolName, request.schoolId)">
                  View School
                  <feather-icon icon="ArrowRightIcon" size="14" />
                </b-link>
                <span v-else class="text-muted">-</span>
              </b-td>
            </b-tr>

            <b-tr v-if="!loading && !filteredRequests.length">
              <b-td colspan="9">
                <div class="text-center py-5">
                  <b-avatar variant="light-secondary" size="56" class="mb-1">
                    <feather-icon icon="InboxIcon" size="26" />
                  </b-avatar>
                  <h5 class="mb-25">No {{ status ? currentTab.label.toLowerCase() : "" }} requests</h5>
                  <small class="text-muted">
                    {{ searchQuery ? "Nothing matches your search." : "New school requests from the app will show up here." }}
                  </small>
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BButton,
  BLink,
  BBadge,
  BAvatar,
  BOverlay,
  BTableSimple,
  BThead,
  BTr,
  BTh,
  BTd,
  BTbody,
  VBTooltip,
} from "bootstrap-vue";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddNewSchool from "./app/list/users-list/AddNewSchool";

const STATUS_TABS = [
  { value: "pending", countKey: "pending", label: "Pending", variant: "warning", color: "#ff9f43", icon: "ClockIcon" },
  { value: "approved", countKey: "approved", label: "Approved", variant: "success", color: "#28c76f", icon: "CheckCircleIcon" },
  { value: "rejected", countKey: "rejected", label: "Rejected", variant: "danger", color: "#ea5455", icon: "XCircleIcon" },
  { value: "", countKey: "all", label: "All", variant: "primary", color: "#7367f0", icon: "InboxIcon" },
];

export default {
  components: {
    AddNewSchool,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BButton,
    BLink,
    BBadge,
    BAvatar,
    BOverlay,
    BTableSimple,
    BThead,
    BTr,
    BTh,
    BTd,
    BTbody,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      statusTabs: STATUS_TABS,
      status: "pending",
      requests: [],
      counts: { pending: 0, approved: 0, rejected: 0, all: 0 },
      loading: false,
      searchQuery: "",
      isAddNewSchoolSidebarActive: false,
      prefill: null,
    };
  },
  computed: {
    currentTab() {
      return this.statusTabs.find((t) => t.value === this.status) || this.statusTabs[0];
    },
    filteredRequests() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.requests;
      return this.requests.filter((r) =>
        [r.schoolName, r.contactName, r.contactPhone, r.city, r.packageName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  created() {
    this.getRequests();
  },
  methods: {
    authHeaders() {
      return {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("_t"),
        },
      };
    },
    getRequests() {
      this.loading = true;
      const query = this.status ? `?status=${this.status}` : "";
      axios
        .get(process.env.VUE_APP_API_URL + `/school/requests${query}`, this.authHeaders())
        .then((response) => {
          this.requests = response.data.requests || [];
          this.counts = { ...this.counts, ...(response.data.counts || {}) };
        })
        .catch((error) => {
          this.toast(this.errorMessage(error, "Could not load school requests"), "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeStatus(status) {
      if (this.status === status) return;
      this.status = status;
      this.getRequests();
    },
    isPending(request) {
      return !request.status || request.status === "pending";
    },
    statusVariant(status) {
      if (status === "approved") return "success";
      if (status === "rejected") return "danger";
      return "warning";
    },
    openCreateSchool(request) {
      this.prefill = {
        request_id: request._id,
        school_name: request.schoolName || "",
        staff_count: request.staffCount || null,
        premium_seats: request.premiumSeats || 0,
        package_id: request.packageId || null,
        package_name: request.packageName || null,
      };
      this.isAddNewSchoolSidebarActive = true;
    },
    createSchoolFromRequest(schoolData) {
      axios
        .post(process.env.VUE_APP_API_URL + "/school/create", schoolData, this.authHeaders())
        .then((response) => {
          if (response.data.message == "School created successfully") {
            this.toast(`School "${schoolData.school_name}" created and request approved`, "success");
          } else {
            this.toast(response.data.message || "School not added", "danger");
          }
          this.prefill = null;
          this.getRequests();
        })
        .catch((error) => {
          this.toast(this.errorMessage(error, "School not added"), "danger");
          this.getRequests();
        });
    },
    rejectRequest(request) {
      Swal.fire({
        title: "Reject request?",
        html: `Reject the request for <strong>${this.escapeHtml(request.schoolName || "this school")}</strong>. The reason is saved with the request.`,
        icon: "warning",
        input: "textarea",
        inputPlaceholder: "Reason for rejecting...",
        inputAttributes: { maxlength: 1000 },
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#ea5455",
        cancelButtonColor: "#82868b",
        showLoaderOnConfirm: true,
        inputValidator: (value) => {
          if (!value || !value.trim()) return "Please enter a reason";
          return undefined;
        },
        preConfirm: (reason) =>
          axios
            .post(
              process.env.VUE_APP_API_URL + "/school/requests/reject",
              { request_id: request._id, reason: reason.trim() },
              this.authHeaders()
            )
            .then((response) => response.data)
            .catch((error) => {
              Swal.showValidationMessage(this.errorMessage(error, "Could not reject the request"));
            }),
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          this.toast("School request rejected", "success");
          this.getRequests();
        }
      });
    },
    sendPropsToSchoolGrade(schoolName, schoolId) {
      localStorage.setItem("schoolName", JSON.stringify(schoolName));
      localStorage.setItem("schoolid", JSON.stringify(schoolId));
    },
    formatDate(date) {
      if (!date) return "-";
      const d = moment(date);
      return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : "-";
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString();
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
    },
    escapeHtml(text) {
      const div = document.createElement("div");
      div.innerText = text;
      return div.innerHTML;
    },
    errorMessage(error, fallback) {
      const data = error && error.response && error.response.data;
      if (data) {
        if (data.errors) {
          const first = Object.values(data.errors)[0];
          if (Array.isArray(first) && first.length) return first[0];
        }
        if (data.message) return data.message;
        if (data.error) return data.error;
      }
      return fallback;
    },
    toast(text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Notification",
          icon: "BellIcon",
          text,
          variant,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.status-filter-card {
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 18px rgba(34, 41, 47, 0.12);
  }

  &--active {
    border-width: 1px;
    border-style: solid;
  }
}

.request-note {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.school-requests-table td {
  vertical-align: middle;
}
</style>
